<script>
import Vue from 'vue'
import {BRow, BCol, BCard, BCardTitle} from 'bootstrap-vue'
import {i18nApi, LegislationApi, ProductApi} from "@core/api";
import Overlay from "@core/components/common/Overlay.vue";
import GeneralInfo from "@/views/administration/legislation/components/GeneralInfo.vue";
import BusinessRules from "@/views/administration/legislation/components/BusinessRules.vue";
import EventStatuses from "@/views/administration/legislation/components/EventStatuses.vue";
import Actions from "@/views/administration/legislation/components/Actions.vue";
import LegalRoles from "@/views/administration/legislation/components/LegalRoles.vue";
import GeneralProductStatementsSelector
    from "@/views/administration/legislation/components/GeneralProductStatementsSelector.vue";
import backendResponse from "@core/mixins/ui/backend-response";
import {ProductCategorySelect} from "@core/components/product";
import QuestionCatalogSelector from "@core/components/legislation";

export default {
    mixins: [backendResponse],
    props: {
        id: {}
    },
    components: {
        ProductCategorySelect,
        Overlay, BRow, BCol, BCard, BCardTitle, GeneralInfo, BusinessRules, EventStatuses, Actions, LegalRoles,
        GeneralProductStatementsSelector, QuestionCatalogSelector
    },
    data() {
        return {
            showLoading: true,
            verificationDocumentTypes: [],
            availableGeneralProductStatements: [],

            legislation: {
                title: '',
                shortTitle: null,
                description: null,
                validFrom: null,
                validTo: null,
                regulationType: null,
                regulationId: null,
                coveredWeight: null,

                productCategories: [],

                //  by reference in general info
                translations: null,

                key: 'generic',

                verificationDocumentTypes: [],
                mandatoryVerificationDocumentTypes: [],
                substanceList: null,
                legalRoleBusinessRoles: [],
                legalRoles: [],
                generalProductStatements: [],
                questionIds: [],
                expressions: [],
                eventStatuses: {},
                requirements: {
                    requiresProductWeight: false
                }
            }
        };
    },
    methods: {
        close() {
            this.$router.go(-1);
        },
        save() {
            this.showLoading = true;
            (this.id
                ? LegislationApi.updateLegislation(this.id, this.gatherSaveAttributes())
                : LegislationApi.createLegislation(this.gatherSaveAttributes()))
                .then(r => {
                    this.showDefaultSuccessMessage('Legislation has been saved');
                    if (!this.id) {
                        this.$router.push({
                            name: 'administration-legislations'
                        });
                    }
                })
                .catch(this.handleBackendError)
                .finally(() => this.showLoading = false)
        },
        onGeneralInfoChanged(info) {
            Object.keys(info).map(key => {
                this.legislation[key] = info[key];
            });
        },
        onProductCategoryChange(categories) {
            this.legislation.productCategories = categories.items.map(i => i.category);
        },
        onGeneralProductStatementsChange(selectedIds) {
            this.legislation.generalProductStatements = selectedIds;
        },
        onBusinessRolesChangedChanged(legalRoleBusinessRole, businessRoles) {
            const index = this.legislation.legalRoleBusinessRoles
                .findIndex(i => i.legalRole.id === legalRoleBusinessRole.legalRole.id);

            if (index >= 0) {
                this.legislation.legalRoleBusinessRoles[index].businessRoles = businessRoles;
            }
        },
        removeLegalRole(role) {
            const index = this.legislation.legalRoles.findIndex(lr => lr.id === role.id);
            if (index >= 0) {
                this.legislation.legalRoles.splice(index, 1);

                const brIndex = this.legislation.legalRoleBusinessRoles.findIndex(i => i.legalRole.id === role.id);
                if (brIndex >= 0) {
                    this.legislation.legalRoleBusinessRoles.splice(brIndex, 1);
                }
            }
        },
        saveLegalRole(role) {
            const index = this.legislation.legalRoles.findIndex(lr => lr.id === role.id);

            if (index >= 0) {
                this.legislation.legalRoles[index].title = role.title;
                this.legislation.legalRoles[index].translations = role.translations;
            } else {
                this.legislation.legalRoles.push(role);
                this.legislation.legalRoleBusinessRoles.push({
                    legalRole: role,
                    businessRoles: []
                });
            }
        },

        gatherSaveAttributes() {
            const attributes = {...this.legislation};

            attributes.substanceList = attributes.substanceList ? attributes.substanceList.id : null;
            attributes.productCategories = attributes.productCategories.map(c => c.id);
            attributes.questionIds = attributes.questionIds instanceof Array ? attributes.questionIds : [];
            attributes.legalRoles = attributes.legalRoles.map(r => this.prepareLegalRoleBeforeSave(r));
            attributes.legalRoleBusinessRoles = attributes.legalRoleBusinessRoles.map(legalRoleBusinessRole => {
                return {
                    legalRole: this.prepareLegalRoleBeforeSave(legalRoleBusinessRole.legalRole),
                    businessRoles: legalRoleBusinessRole.businessRoles.map(role => role.id)
                }
            });

            return attributes;
        },
        normalizeGeneralProductStatements() {
            if (!(this.legislation.generalProductStatements instanceof Array)) {
                this.legislation.generalProductStatements = [];
                return;
            }
            this.legislation.generalProductStatements = this.legislation.generalProductStatements.map(statement => {
                if (typeof statement === 'number' || typeof statement === 'string') {
                    return statement;
                }
                if (statement && statement.id) {
                    return statement.id;
                }
                if (statement && statement.statement && statement.statement.id) {
                    return statement.statement.id;
                }
                return null;
            }).filter(statementId => statementId !== null);
        },
        normalizeQuestionIds() {
            if (!(this.legislation.questionIds instanceof Array)) {
                this.legislation.questionIds = [];
            }
        },

        prepareLegalRoleBeforeSave(role) {
            const prepared = {...role};
            if (prepared.__new) {
                delete prepared.__new;
                delete prepared.id;
            }
            return prepared;
        },

        addEvent(event) {
            if (this.legislation.eventStatuses instanceof Array) {
                this.legislation.eventStatuses = Vue.observable({});
            }
            this.legislation.eventStatuses[event.id] = null;
        }
    },
    created() {
        const generalProductStatementsPromise = ProductApi.getAllGeneralProductStatements()
            .then(res => {
                this.availableGeneralProductStatements = res.data.map(statement => {
                    return {
                        id: statement.id,
                        title: statement.organizationStatement || statement.productStatement
                    };
                });
            });

        if (this.id) {
            Promise.all([
                generalProductStatementsPromise,
                LegislationApi
                    .card(this.id)
                    .then(r => {
                        this.legislation = r.data;
                        this.normalizeGeneralProductStatements();
                        this.normalizeQuestionIds();
                    })
            ]).finally(() => this.showLoading = false);
        } else {
            Promise.all([
                generalProductStatementsPromise,
                i18nApi.getLanguages()
            ]).then(([_, r]) => {
                this.legislation.translations = {};
                r.data.forEach(language => {
                    this.legislation.translations[language.code] = {
                        title: '',
                        shortTitle: '',
                        description: '',
                        legislationInfoHeader: '',
                        legislationInfo: '',
                        listInfo: '',
                    };
                });
            }).finally(() => this.showLoading = false);
        }
    }
}
</script>

<template>
    <Overlay :show="showLoading">
        <b-row class="pb-2">
            <b-col>
                <h2 class="float-left pr-1 mb-1">
                    {{ $t(id ? 'Page(LegislationCard):Component(Text):edit' : 'Page(LegislationCard):Component(Text):create', {legislation: legislation.title}) }}
                </h2>
                <Actions
                    class="float-right"
                    @close="close"
                    @save="save"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <GeneralInfo
                    :legislation="legislation"
                    @input="onGeneralInfoChanged"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <ProductCategorySelect
                    border
                    :only-selectable="false"
                    :selected-items="legislation.productCategories"
                    @change="onProductCategoryChange"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <GeneralProductStatementsSelector
                    :statements="availableGeneralProductStatements"
                    :selected-ids="legislation.generalProductStatements"
                    @input="onGeneralProductStatementsChange"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <QuestionCatalogSelector
                    v-model="legislation.questionIds"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <LegalRoles
                    :legal-roles="legislation.legalRoles"
                    :legal-role-business-roles="legislation.legalRoleBusinessRoles"
                    @saveLegalRole="saveLegalRole"
                    @businessRolesChanged="onBusinessRolesChangedChanged"
                    @remove="removeLegalRole"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <EventStatuses
                    :legislation="legislation"
                    @add="addEvent"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <BusinessRules
                    :legislation="legislation"
                />
            </b-col>
        </b-row>
    </Overlay>
</template>
